import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@comps/ui/select";
import { EQUAL, Filter, IS_EMPTY, IS_NOT_EMPTY, Operator, OR } from "@dto/records";
import { getFieldKey, getOperatorLabel } from "@utils/model";
import { TW_FilterEditorOperatorSelect, TW_FilterEditorOperatorSelectFull, TW_FilterEditorValue_Wrapper } from "@utils/twClasses";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useModelStore } from "src/store";
import { PrimitiveSelect } from "./PrimiteSelect";
import { FieldType } from "@dto/fields";

interface PrimitiveFilterValueSelectorProps {
  selectedPathKey: string | undefined;
  filter: Filter | null;
  validOperators: Operator[];
  validateFilter: (filter: Filter) => void;
  disabled: boolean
}

export function PrimitiveFilterValueSelector({ selectedPathKey, filter, validOperators, validateFilter, disabled }: PrimitiveFilterValueSelectorProps) {

  const { t } = useTranslation();
  const [selectedOperator, setSelectedOperator] = useState<Operator | undefined>(EQUAL);
  const [selectedValue, setSelectedValue] = useState<any>(undefined);
  const fieldByKeys = useModelStore((state) => state.fieldByKeys);

  const fieldKey = selectedPathKey ? getFieldKey(selectedPathKey) : null;
  const field = fieldKey ? (fieldByKeys?.get(fieldKey.fieldKey) || null) : null;
  

  useEffect(() => {
    if (filter) {
      const fieldFilter = filter.groupFilter;
      const filters = fieldFilter?.filters;
      if (filters) {
        const firstFilter = filters[0];
        const value = firstFilter.singleFilter?.value;
        setSelectedOperator(firstFilter.singleFilter?.operator);
        setSelectedValue(value);
      }
    }
  }, [filter]);

  useEffect(() => {
    const value = filter?.groupFilter?.filters[0]?.singleFilter?.value;
    // Initialize boolean field with false value when field changes
    if (field?.type === FieldType.Boolean && 
        selectedValue === undefined && 
        value === undefined &&
        selectedPathKey && 
        selectedOperator) {
      onFilterChange(selectedPathKey, selectedOperator, false);
    }
  }, [selectedPathKey]);


  const onOperatorChange = (operator: Operator) => {
    setSelectedOperator(operator);
    if (selectedPathKey && operator && (selectedValue || operator === IS_EMPTY || operator === IS_NOT_EMPTY)) {
      onFilterChange(selectedPathKey, operator, selectedValue || undefined);
    }
  }

  const onValueChange = (value: any) => {
    setSelectedValue(value);
    if (selectedPathKey && selectedOperator) {
      onFilterChange(selectedPathKey, selectedOperator, value);
    }
  }

  function onFilterChange(pathKey: string, operator: Operator, value: any) {
    if (!pathKey || !operator || (value === undefined && operator !== IS_EMPTY && operator !== IS_NOT_EMPTY)) {
      return;
    }

    const newFieldFilters: Filter[] = [];
    if (operator === IS_EMPTY || operator === IS_NOT_EMPTY) {
      newFieldFilters.push({
        singleFilter: {
          pathKey: pathKey,
          operator: operator,
          value: null,
          valueKey: null,
          formula: null
        },
        groupFilter: null
      });
    } else {
      newFieldFilters.push({
        singleFilter: {
          pathKey: pathKey,
          operator: operator,
          value: value,
          valueKey: null,
          formula: null
        },
        groupFilter: null
      });
    }
    const fieldFilter: Filter = {
      singleFilter: null,
      groupFilter: {
        filters: newFieldFilters,
        combinator: OR
      }
    }

    validateFilter(fieldFilter);
  }

  const isDisabled = !selectedPathKey || !field || disabled;
  return (
    <div className={TW_FilterEditorValue_Wrapper}>
      <Select onValueChange={onOperatorChange} value={selectedOperator} disabled={isDisabled}>
        <SelectTrigger className={(selectedOperator === IS_EMPTY || selectedOperator === IS_NOT_EMPTY) ? TW_FilterEditorOperatorSelectFull : TW_FilterEditorOperatorSelect} disabled={isDisabled}>
          <SelectValue placeholder={t("filter.operator.placeholder")} />
        </SelectTrigger>
        <SelectContent>
          {validOperators.map((operator) => {
            return (
              <SelectItem key={operator} value={operator}>{getOperatorLabel(operator, t)}</SelectItem>
            )
          })}
        </SelectContent>
      </Select>

      {selectedOperator !== IS_EMPTY && selectedOperator !== IS_NOT_EMPTY &&
        <PrimitiveSelect pathKey={selectedPathKey ?? null} field={field} value={selectedValue} disabled={isDisabled} onValueChange={onValueChange} />
      }
    </div>
  )
}